import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';
import type { ParsedValues } from './validation';

export const APP_NAME = 'iBlast';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
export const API_PATH = '/iBlast/';

export type APIPayload = Omit<ParsedValues, 'project' | 'title'>;

export type ChartKey = 'load' | 'scaled_positive' | 'scaled_negative' | 'ce' | 'roof_scaled_rise_time' | 'roof_scaled_duration_roof' | 'q0' | 'pressure_reflection' | 'impulse_reflection';

export const PRINT_CHART_ORDER: ChartKey[] = ['load', 'scaled_positive', 'scaled_negative', 'ce', 'roof_scaled_rise_time', 'roof_scaled_duration_roof', 'q0', 'pressure_reflection', 'impulse_reflection'];

export const PRINT_CHART_ASPECT = {
  load: 'wide',
  scaled_positive: 'square',
  scaled_negative: 'square',
  ce: 'square',
  roof_scaled_rise_time: 'square',
  roof_scaled_duration_roof: 'square',
  q0: 'square',
  pressure_reflection: 'square',
  impulse_reflection: 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 22 }, x: 0.5, xanchor: 'center' },
      margin: { l: 90, r: 28, t: 65, b: 125 },
      xaxis: { title: { font: { size: 24 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 24 } }, tickfont: { size: 18 } },
      legend: { orientation: 'h', font: { size: 20 }, x: 0.5, xref: 'container', xanchor: 'center', y: 0.01, yref: 'container', yanchor: 'bottom' }
    }
  },
  square: {
    width: 800,
    height: 800,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 24 }, x: 0.5, xanchor: 'center' },
      margin: { l: 100, r: 32, t: 85, b: 180 },
      xaxis: { title: { font: { size: 22 } }, tickfont: { size: 17 } },
      yaxis: { title: { font: { size: 22 } }, tickfont: { size: 17 } },
      legend: { orientation: 'h', font: { size: 20 }, x: 0.5, xref: 'container', xanchor: 'center', y: 0.01, yref: 'container', yanchor: 'bottom' }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(22rem, 72vw, 36rem)';

const SQUARE_LIVE_PLOT_SIZE = {
  fit: 'width',
  width: '100%',
  maxWidth: '39rem',
  aspectRatio: '1 / 1',
  titleFontSize: 12,
  legendFontSize: 13,
  plotMargin: { b: 140 },
  legendPlacement: 'plot'
} satisfies LivePlotSizeOptions;

export const LIVE_PLOT_SIZE_BY_KEY = {
  load: {
    fit: 'width',
    width: '100%',
    maxWidth: '39rem',
    aspectRatio: '5 / 3',
    titleFontSize: 16,
    legendFontSize: 13,
    plotMargin: { b: 84 },
    legendPlacement: 'plot'
  },
  scaled_positive: SQUARE_LIVE_PLOT_SIZE,
  scaled_negative: SQUARE_LIVE_PLOT_SIZE,
  ce: SQUARE_LIVE_PLOT_SIZE,
  roof_scaled_rise_time: SQUARE_LIVE_PLOT_SIZE,
  roof_scaled_duration_roof: SQUARE_LIVE_PLOT_SIZE,
  q0: SQUARE_LIVE_PLOT_SIZE,
  pressure_reflection: SQUARE_LIVE_PLOT_SIZE,
  impulse_reflection: SQUARE_LIVE_PLOT_SIZE
} satisfies Record<ChartKey, LivePlotSizeOptions>;

export type APIResponse = {
  summary?: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
  warnings?: Record<string, string>;
};

// Plotly chart images are generated client-side for print/save.
export type BlastResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

const CHARGE_TYPE_OPTIONS: DropdownOption[] = [
  { value: '0', label: 'Hemispherical' },
  { value: '1', label: 'Spherical' }
];

const CALCULATION_TYPE_OPTIONS: DropdownOption[] = [
  { value: '1', label: 'Charge Weight, Standoff, Angle -> Pressure, Impulse' },
  { value: '2', label: 'Pressure, Impulse, angle -> Charge Weight, Standoff' },
  { value: '3', label: 'Standoff, Pressure, angle -> Charge Weight, Impulse' },
  { value: '4', label: 'Charge Weight, Pressure, angle -> Standoff, Impulse' }
];

const TARGET_TYPE_OPTIONS: DropdownOption[] = [
  { value: '0', label: 'Front Wall' },
  { value: '1', label: 'Side Wall / Roof' },
  { value: '2', label: 'Back Wall' }
];

export const DROPDOWN_OPTIONS = {
  chargeType: CHARGE_TYPE_OPTIONS,
  calculationType: CALCULATION_TYPE_OPTIONS,
  targetType: TARGET_TYPE_OPTIONS
};
